import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  buildLoginPath,
  clearAuthenticatedSession,
  getCurrentDestination,
  markAuthenticatedSession,
} from '@/lib/authSession';
import {
  completeExpiredSession,
  createSessionExpiryController,
} from '@/lib/sessionExpiryController';
import { useAuthStore } from '@/stores/authStore';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function SessionExpiryHandler() {
  const { isLoaded, isSignedIn, sessionId } = useAuth();
  const { setActive, signOut } = useClerk();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const location = useLocation();
  const navigate = useNavigate();
  const expiryControllerRef = useRef(createSessionExpiryController());
  const [recoveryPath, setRecoveryPath] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    if (isLoaded && isSignedIn && sessionId) {
      markAuthenticatedSession(sessionId);
      expiryControllerRef.current.rearm();
    }
  }, [isLoaded, isSignedIn, sessionId]);

  useEffect(() => {
    const handleExpiredSession = async () => {
      if (!expiryControllerRef.current.beginHandling()) return;

      const loginPath = buildLoginPath(getCurrentDestination(location), true);
      clearAuthenticatedSession();
      clearAuth();

      const completed = await completeExpiredSession({
        signOut,
        setActiveSession: (session) => setActive({ session }),
        redirectUrl: loginPath,
      });

      if (completed) {
        navigate(loginPath, { replace: true });
      } else {
        expiryControllerRef.current.rearm();
        setRecoveryPath(loginPath);
      }
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpiredSession);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpiredSession);
  }, [clearAuth, location, navigate, setActive, signOut]);

  const retrySessionCleanup = async () => {
    if (!recoveryPath || isRetrying) return;
    setIsRetrying(true);

    const completed = await completeExpiredSession({
      signOut,
      setActiveSession: (session) => setActive({ session }),
      redirectUrl: recoveryPath,
    });

    if (completed) {
      setRecoveryPath(null);
      navigate(recoveryPath, { replace: true });
    }
    setIsRetrying(false);
  };

  if (recoveryPath) {
    return (
      <Dialog open>
        <DialogContent
          role="alertdialog"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          className="w-[calc(100%-2rem)] max-w-md border-border bg-card text-center"
        >
          <AlertTriangle className="mx-auto mb-4 h-8 w-8 text-primary" />
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle className="text-xl text-foreground">
              Session cleanup interrupted
            </DialogTitle>
            <DialogDescription>
              We could not finish signing you out. Retry before continuing to keep your account
              secure.
            </DialogDescription>
          </DialogHeader>
          <Button
            type="button"
            onClick={retrySessionCleanup}
            disabled={isRetrying}
            className="mx-auto mt-2 gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Retrying...' : 'Retry sign out'}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
}
